'use client';

import { useState } from 'react';
import { Plus, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { SingleDatePicker } from '@/features/invoices/single-date-picker';
import { DatePickerWithRange } from '@/features/invoices/date-range';
import { formatDateForInput } from '@/lib/date-formatter';
import { BuilderState, BuilderItem } from './types';

interface BuilderFormProps {
  state: BuilderState;
  onChange: (state: BuilderState) => void;
  saveDraftEnabled: boolean;
  onSaveDraftToggle: (enabled: boolean) => void;
  cookiesAccepted: boolean;
  t: (key: string) => string;
}

const CURRENCIES = ['EUR', 'USD', 'RSD', 'GBP'] as const;
const INVOICE_TYPES = ['Invoice', 'Proforma Invoice', 'Offer'] as const;
const TOTAL_STEPS = 5;

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

export function BuilderForm({
  state,
  onChange,
  saveDraftEnabled,
  onSaveDraftToggle,
  cookiesAccepted,
  t,
}: BuilderFormProps) {
  const [currentStep, setCurrentStep] = useState(1);

  function set<K extends keyof BuilderState>(key: K, value: BuilderState[K]) {
    onChange({ ...state, [key]: value });
  }

  function setItem(
    index: number,
    field: keyof BuilderItem,
    value: string | number
  ) {
    const items = state.items.map((item, i) => {
      if (i !== index) return item;
      return { ...item, [field]: value };
    });
    onChange({ ...state, items });
  }

  function addItem() {
    onChange({
      ...state,
      items: [
        ...state.items,
        { description: '', qty: 1, unit: 'pcs', price: 0, tax: 0 },
      ],
    });
  }

  function removeItem(index: number) {
    if (state.items.length === 1) return;
    onChange({ ...state, items: state.items.filter((_, i) => i !== index) });
  }

  const inputClass = 'h-8 text-sm';
  const selectClass =
    'h-8 text-sm w-full rounded-md border border-input bg-background px-3 py-1 shadow-sm focus:outline-none focus:ring-1 focus:ring-ring';

  const stepTitles = [
    t('step1Title'),
    t('step2Title'),
    t('step3Title'),
    t('step4Title'),
    t('step5Title'),
  ];

  const stepLabel = t('stepOf')
    .replace('{current}', String(currentStep))
    .replace('{total}', String(TOTAL_STEPS));

  return (
    <div className="space-y-5 text-sm">
      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {stepLabel}:{' '}
            <span className="font-medium text-foreground">
              {stepTitles[currentStep - 1]}
            </span>
          </span>
          <span>
            {currentStep}/{TOTAL_STEPS}
          </span>
        </div>
        <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-500 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
          />
        </div>
      </div>

      {/* Step 1: Your Business */}
      {currentStep === 1 && (
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('companyName')}>
            <Input
              className={inputClass}
              value={state.companyName}
              onChange={(e) => set('companyName', e.target.value)}
              placeholder="Acme Ltd."
            />
          </Field>
          <Field label={t('taxId')}>
            <Input
              className={inputClass}
              value={state.taxId}
              onChange={(e) => set('taxId', e.target.value)}
              placeholder="SI12345678"
            />
          </Field>
          <Field label={t('address')}>
            <Input
              className={inputClass}
              value={state.companyAddress}
              onChange={(e) => set('companyAddress', e.target.value)}
              placeholder="123 Main St"
            />
          </Field>
          <Field label={t('zipCode')}>
            <Input
              className={inputClass}
              value={state.zipCode}
              onChange={(e) => set('zipCode', e.target.value)}
              placeholder="1000"
            />
          </Field>
          <Field label={t('city')}>
            <Input
              className={inputClass}
              value={state.city}
              onChange={(e) => set('city', e.target.value)}
              placeholder="Ljubljana"
            />
          </Field>
          <Field label={t('country')}>
            <Input
              className={inputClass}
              value={state.country}
              onChange={(e) => set('country', e.target.value)}
              placeholder="Slovenia"
            />
          </Field>
        </div>
      )}

      {/* Step 2: Client Info */}
      {currentStep === 2 && (
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('clientName')}>
            <Input
              className={inputClass}
              value={state.clientName}
              onChange={(e) => set('clientName', e.target.value)}
              placeholder="Client Corp"
            />
          </Field>
          <Field label={t('clientTaxNumber')}>
            <Input
              className={inputClass}
              value={state.clientTaxNumber}
              onChange={(e) => set('clientTaxNumber', e.target.value)}
              placeholder="SI87654321"
            />
          </Field>
          <Field label={t('clientAddress')}>
            <Input
              className={inputClass}
              value={state.clientAddress}
              onChange={(e) => set('clientAddress', e.target.value)}
              placeholder="456 Client Ave"
            />
          </Field>
          <Field label={t('zipCode')}>
            <Input
              className={inputClass}
              value={state.clientZipCode}
              onChange={(e) => set('clientZipCode', e.target.value)}
              placeholder="1000"
            />
          </Field>
          <Field label={t('clientCity')}>
            <Input
              className={inputClass}
              value={state.clientCity}
              onChange={(e) => set('clientCity', e.target.value)}
              placeholder="Maribor"
            />
          </Field>
          <Field label={t('clientCountry')}>
            <Input
              className={inputClass}
              value={state.clientCountry}
              onChange={(e) => set('clientCountry', e.target.value)}
              placeholder="Slovenia"
            />
          </Field>
        </div>
      )}

      {/* Step 3: Invoice Details */}
      {currentStep === 3 && (
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('invoiceNumber')}>
            <Input
              className={inputClass}
              value={state.invoiceNumber}
              onChange={(e) => set('invoiceNumber', e.target.value)}
            />
          </Field>
          <Field label={t('invoiceType')}>
            <select
              className={selectClass}
              value={state.invoiceType}
              onChange={(e) => set('invoiceType', e.target.value)}
            >
              {INVOICE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </Field>
          <div className="col-span-2">
            <Field label={t('serviceDate')}>
              <DatePickerWithRange
                value={{
                  from: state.serviceFrom
                    ? new Date(state.serviceFrom)
                    : undefined,
                  to: state.serviceTo ? new Date(state.serviceTo) : undefined,
                }}
                onChange={(range) => {
                  set(
                    'serviceFrom',
                    range.from ? formatDateForInput(range.from) : ''
                  );
                  set(
                    'serviceTo',
                    range.to ? formatDateForInput(range.to) : ''
                  );
                }}
                placeholder={t('selectDateRange')}
              />
            </Field>
          </div>
          <Field label={t('issuedDate')}>
            <SingleDatePicker
              value={state.issuedDate ? new Date(state.issuedDate) : null}
              onChange={(d) =>
                set('issuedDate', d ? formatDateForInput(d) : '')
              }
            />
          </Field>
          <Field label={t('dueDate')}>
            <SingleDatePicker
              value={state.dueDate ? new Date(state.dueDate) : null}
              onChange={(d) => set('dueDate', d ? formatDateForInput(d) : '')}
            />
          </Field>
        </div>
      )}

      {/* Step 4: Line Items */}
      {currentStep === 4 && (
        <div className="space-y-4">
          <div className="space-y-2">
            <div
              className="grid gap-1 text-xs text-muted-foreground font-medium"
              style={{ gridTemplateColumns: '3fr 1fr 1fr 1.5fr 1fr auto' }}
            >
              <span>{t('description')}</span>
              <span>{t('qty')}</span>
              <span>{t('unit')}</span>
              <span>{t('price')}</span>
              <span>{t('taxPct')}</span>
              <span />
            </div>

            {state.items.map((item, index) => (
              <div
                key={index}
                className="grid gap-1 items-center"
                style={{ gridTemplateColumns: '3fr 1fr 1fr 1.5fr 1fr auto' }}
              >
                <Input
                  className={inputClass}
                  value={item.description}
                  onChange={(e) =>
                    setItem(index, 'description', e.target.value)
                  }
                  placeholder={t('descPlaceholder')}
                />
                <Input
                  className={inputClass}
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.qty}
                  onChange={(e) =>
                    setItem(index, 'qty', parseFloat(e.target.value) || 0)
                  }
                />
                <Input
                  className={inputClass}
                  value={item.unit}
                  onChange={(e) => setItem(index, 'unit', e.target.value)}
                />
                <Input
                  className={inputClass}
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.price}
                  onChange={(e) =>
                    setItem(index, 'price', parseFloat(e.target.value) || 0)
                  }
                />
                <Input
                  className={inputClass}
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={item.tax}
                  onChange={(e) =>
                    setItem(index, 'tax', parseFloat(e.target.value) || 0)
                  }
                />
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  disabled={state.items.length === 1}
                  className="p-1 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-30"
                  aria-label={t('remove')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addItem}
              className="mt-1 h-7 text-xs gap-1"
            >
              <Plus className="w-3 h-3" />
              {t('addItem')}
            </Button>
          </div>
        </div>
      )}

      {/* Step 5: Payment & Contact */}
      {currentStep === 5 && (
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('currency')}>
            <select
              className={selectClass}
              value={state.currency}
              onChange={(e) =>
                set('currency', e.target.value as BuilderState['currency'])
              }
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('email')}>
            <Input
              className={inputClass}
              type="email"
              value={state.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder="you@company.com"
            />
          </Field>
          <Field label={t('bankName')}>
            <Input
              className={inputClass}
              value={state.bankName}
              onChange={(e) => set('bankName', e.target.value)}
              placeholder="NLB"
            />
          </Field>
          <Field label={t('accountNumber')}>
            <Input
              className={inputClass}
              value={state.accountNumber}
              onChange={(e) => set('accountNumber', e.target.value)}
              placeholder="SI56 0000 0000 0000 000"
            />
          </Field>
          <Field label={t('bic')}>
            <Input
              className={inputClass}
              value={state.bic}
              onChange={(e) => set('bic', e.target.value)}
              placeholder="LJBASI2X"
            />
          </Field>

          {/* Save draft toggle — on last step */}
          <div className="col-span-2 flex items-center gap-3 p-3 rounded-lg bg-muted/40 border mt-1">
            <Switch
              id="save-draft"
              checked={saveDraftEnabled}
              onCheckedChange={onSaveDraftToggle}
              disabled={!cookiesAccepted}
            />
            <div>
              <Label
                htmlFor="save-draft"
                className="text-sm font-medium cursor-pointer"
              >
                {t('saveDraft')}
              </Label>
              <p className="text-xs text-muted-foreground">
                {!cookiesAccepted
                  ? t('saveDraftNeedsCookies')
                  : saveDraftEnabled
                    ? t('saveDraftActive')
                    : t('saveDraftHint')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setCurrentStep((s) => Math.max(1, s - 1))}
          disabled={currentStep === 1}
          className="gap-1"
        >
          <ChevronLeft className="w-4 h-4" />
          {t('stepBack')}
        </Button>
        {currentStep < TOTAL_STEPS && (
          <Button
            type="button"
            size="sm"
            onClick={() => setCurrentStep((s) => Math.min(TOTAL_STEPS, s + 1))}
            className="gap-1"
          >
            {t('stepNext')}
            <ChevronRight className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
